/**
 * A pure model of WebAudio AudioParam automation (setValueAtTime, linear and
 * exponential ramps), used to reschedule fades *without clicks*.
 *
 * Why: cancelling a ramp that is in progress makes an AudioParam jump back to
 * its previous event value, and `cancelAndHoldAtTime` is not available in every
 * browser. Instead, each automated param has a ParamTrack that knows exactly
 * what curve it scheduled, so a new fade can start from the precise current
 * value: cancel from `now`, anchor `set(valueAt(now), now)`, then ramp on.
 *
 * Pure and DOM-free (unit-tested in node).
 */

export type AutomationEvent =
  | { type: 'set'; time: number; value: number }
  | { type: 'linear'; time: number; value: number }
  | { type: 'exp'; time: number; value: number };

/** The subset of AudioParam used for scheduled automation. */
export interface AutomatableParam {
  cancelScheduledValues(time: number): unknown;
  setValueAtTime(value: number, time: number): unknown;
  linearRampToValueAtTime(value: number, time: number): unknown;
  exponentialRampToValueAtTime(value: number, time: number): unknown;
}

/**
 * Value of a param at time `t` given its events (sorted by time), following
 * the WebAudio rules: a ramp interpolates from the previous event's time and
 * value to its own; a set holds until the next event; an exponential ramp
 * from 0 or across a sign change holds the previous value until its end time.
 */
export function automationValueAt(events: readonly AutomationEvent[], t: number, initial: number): number {
  let prevTime = -Infinity;
  let prevValue = initial;
  for (const e of events) {
    if (t < e.time) {
      if (e.type === 'set' || !Number.isFinite(prevTime) || e.time <= prevTime) return prevValue;
      const f = (t - prevTime) / (e.time - prevTime);
      if (e.type === 'linear') return prevValue + (e.value - prevValue) * f;
      if (prevValue === 0 || prevValue * e.value < 0) return prevValue;
      return prevValue * (e.value / prevValue) ** f;
    }
    prevTime = e.time;
    prevValue = e.value;
  }
  return prevValue;
}

/** Tracks the automation scheduled on one AudioParam so fades can be re-planned continuously. */
export class ParamTrack {
  private events: AutomationEvent[] = [];

  constructor(private readonly initial: number) {}

  valueAt(t: number): number {
    return automationValueAt(this.events, t, this.initial);
  }

  /** The events currently planned (for tests and debugging). */
  get planned(): readonly AutomationEvent[] {
    return this.events;
  }

  /**
   * Replace the plan with `planned`, mirroring `cancelScheduledValues(now)`
   * followed by the new events. `planned` must start with an anchor
   * `set(valueAt(now), now)`: everything before it is superseded, so older
   * events are dropped (this also keeps the list from growing without bound).
   * Returns the new plan, ready for writeAutomation().
   */
  replace(planned: readonly AutomationEvent[]): AutomationEvent[] {
    this.events = planned.slice().sort((a, b) => a.time - b.time);
    return this.events;
  }

  /**
   * Plan a continuous ramp to `value`: the param keeps following its current
   * curve until `start` (≥ now), then ramps to `value`, arriving at `end`.
   * An exponential ramp is used only when both ends are > 0 (WebAudio rule).
   */
  rampTo(value: number, now: number, start: number, end: number, curve: 'linear' | 'exp' = 'linear'): AutomationEvent[] {
    const s = Math.max(now, start);
    const e = Math.max(s, end);
    const vNow = this.valueAt(now);
    const vStart = this.valueAt(s);
    const planned: AutomationEvent[] = [{ type: 'set', time: now, value: vNow }];
    if (s > now) planned.push({ type: 'linear', time: s, value: vStart });
    const useExp = curve === 'exp' && value > 0 && vStart > 0;
    planned.push({ type: useExp ? 'exp' : 'linear', time: e, value });
    return this.replace(planned);
  }
}

/** Write planned events to a real AudioParam (cancelling anything from `now`). */
export function writeAutomation(param: AutomatableParam, now: number, events: readonly AutomationEvent[]): void {
  param.cancelScheduledValues(now);
  for (const e of events) {
    if (e.type === 'set') param.setValueAtTime(e.value, e.time);
    else if (e.type === 'linear') param.linearRampToValueAtTime(e.value, e.time);
    else param.exponentialRampToValueAtTime(e.value, e.time);
  }
}
